<script setup>
// Архів ефірів: записи трансляцій цілком. З'являються самі після кожного
// ефіру (streaming/archive-worker.sh), а адміни на сторінці запису
// перейменовують, відкривають чи закривають доступ і видаляють.
// Сортування й пошук живуть в адресі — як у кліпів.

const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const token = useStrapiToken()

const PAGE = 24

const SORTS = [
  { id: 'new', label: 'Нові' },
  { id: 'views', label: 'Перегляди' },
  { id: 'old', label: 'Старі' }
]

const sort = computed(() => (SORTS.some((s) => s.id === route.query.sort) ? route.query.sort : 'new'))
const search = ref(String(route.query.q || ''))

const setQuery = (patch) => {
  const query = { ...route.query, ...patch }
  for (const key of Object.keys(query)) if (query[key] === '' || query[key] == null) delete query[key]
  router.replace({ query })
}

let searchTimer = null
watch(search, (value) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => setQuery({ q: value.trim() }), 350)
})

const records = ref([])
const total = ref(0)
const loading = ref(true)
const loadingMore = ref(false)
const failed = ref(false)

const fetchPage = (offset) =>
  $fetch(`${config.public.apiBase}/api/archives`, {
    query: {
      sort: sort.value,
      search: String(route.query.q || '') || undefined,
      offset,
      limit: PAGE
    },
    headers: token.value ? { Authorization: `Bearer ${token.value}` } : {}
  })

let generation = 0
const load = async () => {
  const mineGen = ++generation
  loading.value = true
  try {
    const data = await fetchPage(0)
    if (mineGen !== generation) return
    records.value = data.records
    total.value = data.total
    failed.value = false
  } catch (e) {
    if (mineGen === generation) failed.value = true
  } finally {
    if (mineGen === generation) loading.value = false
  }
}

const loadMore = async () => {
  if (loadingMore.value || records.value.length >= total.value) return
  loadingMore.value = true
  try {
    const data = await fetchPage(records.value.length)
    const seen = new Set(records.value.map((r) => r.id))
    records.value = [...records.value, ...data.records.filter((r) => !seen.has(r.id))]
    total.value = data.total
  } finally {
    loadingMore.value = false
  }
}

watch(() => route.query, load, { deep: true })
watch(token, load)
onMounted(load)

const sentinel = ref(null)
useIntersectionObserver(sentinel, ([entry]) => {
  if (entry?.isIntersecting) loadMore()
}, { rootMargin: '600px' })

useHead({ title: 'Архів ефірів — Олсіор дивиться аніме' })
useSeoMeta({
  description: 'Записи ефірів Olsior цілком — дивитись після трансляції.'
})
</script>

<template>
  <div class="mx-auto px-[10px] py-[20px] max-w-[1350px] min-h-screen">
    <div class="flex flex-wrap items-baseline gap-3 mb-[6px]">
      <h1 class="font-bold text-2xl">Архів ефірів</h1>
      <span
        v-if="total"
        class="text-sm text-base-content/50"
        >{{ total }}</span
      >
    </div>
    <p class="mb-[16px] text-sm text-base-content/50">
      Записи трансляцій цілком. Новий з'являється сам за якийсь час після ефіру.
    </p>

    <div class="flex flex-wrap items-center gap-2 mb-[16px]">
      <div class="inline-flex flex-wrap gap-1 bg-neutral/60 p-1 rounded-lg">
        <button
          v-for="s in SORTS"
          :key="s.id"
          class="px-3 py-1 rounded-md text-sm transition-colors"
          :class="sort === s.id ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:bg-base-content/10'"
          @click="setQuery({ sort: s.id === 'new' ? '' : s.id })"
        >
          {{ s.label }}
        </button>
      </div>

      <span class="flex-1" />

      <input
        v-model="search"
        type="search"
        class="bg-neutral/60 w-full sm:w-64 input input-sm"
        placeholder="Пошук за назвою"
      />
    </div>

    <div
      v-if="loading && !records.length"
      class="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <div
        v-for="n in 8"
        :key="n"
        class="bg-neutral/50 rounded-[12px] aspect-video animate-pulse"
      />
    </div>

    <p
      v-else-if="failed"
      class="py-[40px] text-base-content/50 text-center"
    >
      Не вдалося завантажити архів
    </p>

    <p
      v-else-if="!records.length"
      class="py-[60px] text-base-content/60 text-center"
    >
      {{ route.query.q ? 'Нічого не знайдено' : 'Записів ще немає — перший з\'явиться після найближчого ефіру' }}
    </p>

    <div
      v-else
      class="gap-x-4 gap-y-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      :class="{ 'opacity-60': loading }"
    >
      <ArchiveCard
        v-for="record in records"
        :key="record.id"
        :record="record"
      />
    </div>

    <div
      ref="sentinel"
      class="h-10"
    />
    <p
      v-if="loadingMore"
      class="text-center"
    >
      <span class="loading loading-spinner loading-sm" />
    </p>
  </div>
</template>
